import type { ContentfulPageResponseModel } from '~~/server/api/contentful/index.get';
import { isContentfulPageRedirectResponse } from '~/utils/contentful/isContentfulPageRedirectResponse';

/**
 * True when cached `/api/contentful` data matches the requested id or slug.
 */
export function contentfulPageCacheMatchesRequest(
  data: ContentfulPageResponseModel,
  id: string | undefined,
  slug: string | undefined
): boolean {
  if (isContentfulPageRedirectResponse(data)) {
    return false;
  }

  const pageEntry = data.page;
  if (pageEntry) {
    if (id) {
      return pageEntry.sys?.id === id;
    }

    // slug === undefined means "homepage via GPS id" — never reuse a slug-based cache entry.
    if (slug === undefined) {
      return false;
    }

    const cachedSlug = pageEntry.fields?.slug ?? '';
    return cachedSlug.toLowerCase() === slug.toLowerCase();
  }

  // `data.entry` is the EntryWithLinkData wrapper; the Contentful entry with
  // `sys`/`fields` lives one level deeper at `data.entry.entry`.
  const entry = data.entry?.entry;

  if (id) {
    return entry?.sys?.id === id;
  }

  // slug === undefined means "homepage via GPS id" — never reuse a slug-based cache entry.
  if (slug === undefined) {
    return false;
  }

  if (!entry || !('fields' in entry) || !entry.fields) {
    return false;
  }

  const fields = entry.fields as { slug?: string };
  const cachedSlug = fields.slug ?? '';

  return cachedSlug.toLowerCase() === slug.toLowerCase();
}
